import React, { useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { Add, Close, Delete, Edit } from '@mui/icons-material';

import { ValueBox } from '../../molecules/value-box';

import { Group } from '../../organisms/group';
import { ServiceAccessInput } from '../../organisms/settings/service-access-input';
import { useDispatch, useSelector } from '../../store/utils';

import { Row } from '../../atoms/row';
import { SimpleTable } from '../../molecules/simple-table';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { Variables } from '../../store/types';
import { useConfirm } from 'material-ui-confirm';
import { variablesChange } from '../../store/actions';
import { MqttCloudDeviceType } from '../../shared';
import { CloudPlugsList } from '../../organisms/cloud-plugs-list';

const DevCloudPlugs = () => {
  const [serviceActive, connected] = useSelector(
    ({ variables }) =>
      [variables.SERVICE_ACTIVE, variables.MQTT_CLOUD_CONNECTED] as const
  );

  if (!serviceActive) {
    return (
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="dev_homepage.title"
            defaultMessage="Vývojové menu"
          />
        }
      >
        <ServiceAccessInput />
      </Group>
    );
  }

  return (
    <Group
      layout="list"
      title={
        <FormattedMessage
          id="dev.cloud_plugs.title"
          defaultMessage="Cloudové zásuvky"
        />
      }
    >
      <ValueBox
        type="row"
        title={
          <FormattedMessage
            id="dev.cloud_plugs.mqtt_cloud.connected"
            defaultMessage="Stav MQTT Cloud připojení"
          />
        }
        value={
          connected ? (
            <FormattedMessage
              id="mqtt_cloud.connected.online"
              defaultMessage="Online"
            />
          ) : (
            <FormattedMessage
              id="mqtt_cloud.connected.offline"
              defaultMessage="Offline"
            />
          )
        }
      />

      <CloudPlugsConfig />

      <CloudPlugsList
        title={
          <FormattedMessage
            id="cloud_plugs.list.title"
            defaultMessage="Status"
          />
        }
      />
    </Group>
  );
};

export default DevCloudPlugs;

const CloudPlugsConfig = () => {
  const confirm = useConfirm();

  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState(false);
  const [newValue, setNewValue] = useState<Variables['MQTT_CLOUD_DEVICES']>([]);

  const [cloudDevices] = useSelector(
    ({ variables }) => [variables.MQTT_CLOUD_DEVICES] as const
  );

  const changes = useMemo(
    () => !isEqual(newValue, cloudDevices),
    [newValue, cloudDevices]
  );

  const deleteDevice = (index: number) => {
    setNewValue((prev) => prev.filter((_, i) => i !== index));
  };

  const addDevice = () => {
    setNewValue((prev) => [
      ...prev,
      { id: '', type: MqttCloudDeviceType.ShellyPlusPlugSGen3, plugId: '' },
    ]);
  };

  const saveChanges = () => {
    confirm({
      title: 'Opravdu chcete uložit změny?',
    }).then(
      () => {
        dispatch(variablesChange({ MQTT_CLOUD_DEVICES: newValue }));
        setEditMode(false);
      },
      () => {
        // noop
      }
    );
  };

  return (
    <>
      <SimpleTable
        title={
          <Row gap={0.5}>
            <span>Konfigurace</span>

            {editMode ? (
              <Tooltip title="Zrušit změny">
                <IconButton
                  onClick={() => {
                    setNewValue(cloudDevices);
                    setEditMode(false);
                  }}
                  size="small"
                >
                  <Close />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Upravit">
                <IconButton
                  onClick={() => {
                    setNewValue(cloudDevices);
                    setEditMode(true);
                  }}
                  size="small"
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}

            {editMode && (
              <IconButton
                key={2}
                onClick={() => addDevice()}
                size="small"
                sx={{ mt: 'auto' }}
              >
                <Add />
              </IconButton>
            )}
          </Row>
        }
        rows={[
          ['Typ', 'ID', ''],
          ...(editMode ? newValue : cloudDevices).map(({ id, type }, index) => {
            return [
              editMode ? (
                <MqttDeviceTypeSelect
                  value={type}
                  onChange={(value) => {
                    setNewValue((prev) => {
                      const copy = [...prev];
                      copy[index] = { ...copy[index], type: value };
                      return copy;
                    });
                  }}
                />
              ) : (
                type
              ),
              editMode ? (
                <TextField
                  value={id}
                  size="small"
                  onChange={(e) => {
                    const value = e.target.value as string;
                    setNewValue((prev) => {
                      const copy = [...prev];
                      copy[index] = {
                        ...copy[index],
                        id: value,
                        plugId: value,
                      };
                      return copy;
                    });
                  }}
                />
              ) : (
                id
              ),
              editMode ? (
                <IconButton
                  key={2}
                  onClick={() => deleteDevice(index)}
                  size="small"
                >
                  <Delete />
                </IconButton>
              ) : (
                <></>
              ),
            ];
          }),
        ]}
        firstLineBold
        footer={
          <Box>
            {editMode && changes && (
              <Button
                variant="contained"
                size="small"
                onClick={saveChanges}
                sx={{ mt: 3 }}
              >
                Uložit změny
              </Button>
            )}
          </Box>
        }
        sx={
          editMode
            ? {
                borderColor: 'primary.main',
                borderWidth: 2,
                borderStyle: 'solid',
              }
            : {}
        }
      />
    </>
  );
};

const MqttDeviceTypeSelect = ({
  value,
  onChange,
}: {
  value: MqttCloudDeviceType;
  onChange: (value: MqttCloudDeviceType) => void;
}) => {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value as MqttCloudDeviceType)}
    >
      {Object.entries(MqttCloudDeviceType).map(([key, value]) => {
        return (
          <MenuItem key={key} value={value}>
            {key}
          </MenuItem>
        );
      })}
    </Select>
  );
};
